/**
 * ``TrailLayer`` — non-visual React component that wires a MapLibre
 * GeoJSON source + line layer to the selected Producer's trail
 * fetch. Mounts only when a Producer is selected AND the trail is
 * eligible (Producer moved > 50 m in the 15-min window per ADR-016
 * Open Item 4 default).
 *
 * Stream A PR 2 / commit M5 — REQ-UI-012 polyline rendering. The
 * surface composes:
 *
 *   - :func:`useMapSelectedProducerId` for the active map selection (the
 *     trail follows the ``mapSelected`` slot per the 2026-06-09 two-slot
 *     redesign — a list-row left-click that only inspects must not draw it).
 *   - :func:`useTrail` for the BFF fetch.
 *   - :func:`isTrailEligible` for the threshold gate.
 *   - :file:`trail-layer-impl.ts` helpers for the MapLibre lifecycle.
 *
 * # Why a separate component
 *
 * Same posture as :file:`snapshot-layer.tsx` /
 * :file:`cluster-layer.tsx`: the :class:`Map` component owns the
 * container + the :func:`useMap` lifecycle. Splitting the trail
 * into its own component lets the Map gate the mount on the
 * ``onMapReady`` callback; the trail layer mounts knowing the map
 * is loaded.
 *
 * # Why a separate impl file
 *
 * ESLint's ``react-refresh/only-export-components`` rule. The
 * helpers live in :file:`trail-layer-impl.ts` so Fast Refresh keeps
 * working cleanly on this component during dev.
 *
 * # Eligibility gate
 *
 * The trail layer mounts the MapLibre source + line layer ONLY
 * when both:
 *
 *   1. A Producer is selected.
 *   2. :func:`isTrailEligible` returns ``true`` for the fetched
 *      trail items (> 50 m max-pairwise within the window).
 *
 * When the gate fails (selection cleared OR trail ineligible OR
 * fetch still in flight OR fetch errored), the component detaches
 * any previously-attached layer/source. The marker remains visible
 * (it lives on the snapshot layer); the trail simply disappears.
 *
 * # SSE live-update seam
 *
 * Per the M5 brief + ADR-016 §5, the trail "updates when new
 * positions arrive via the SSE stream" is post-MVP. The M5 hook
 * fetches once on selection-change; the component re-renders on
 * the fetch's lifecycle (loading → success / error). M5+1 will
 * tap the SSE stream and append matching positions to the trail
 * cache via :func:`queryClient.setQueryData`. Flagged in
 * :file:`use-trail.ts` §"SSE live-update seam".
 *
 * Cross-references:
 *   - REQ-UI-012 (trail rendering).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - ADR-016 §5 + Open Items 3, 4, 12.
 *   - docs/engineering/detail/storyboards/flows/02-map-view/README.md.
 */
import { useEffect } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import { useMapSelectedProducerId } from "@/selection/selection-store";
import { isTrailEligible } from "@/map/trail-eligibility";
import { hasMapPosition } from "@/map/snapshot-types";
import { attachTrailLayer, detachTrailLayer } from "@/map/trail-layer-impl";
import { useTrail } from "@/map/use-trail";
import { useDynamicTrack } from "@/map/use-dynamic-track";

/**
 * Props for :func:`TrailLayer`.
 *
 * Cross-references:
 *   - REQ-UI-012.
 */
export interface TrailLayerProps {
  /** Live MapLibre map instance. The caller (:class:`Map`) only
   *  mounts :func:`TrailLayer` after the map's ``load`` event has
   *  fired. */
  map: MapLibreMap;
}

/**
 * Non-visual React component that owns the trail layer's MapLibre
 * lifecycle.
 *
 * Cross-references:
 *   - REQ-UI-012.
 *   - ADR-016 §5.
 */
export function TrailLayer(props: TrailLayerProps): null {
  const { map } = props;
  // The trail follows the MAP selection slot (REQ-UI-012 + the 2026-06-09
  // two-slot redesign): a Platforms-list left-click inspects without
  // drawing a trail; only a map-originated selection (selectOnMap) does.
  const selectedProducerId = useMapSelectedProducerId();
  const trailQuery = useTrail({ producerId: selectedProducerId });
  // The static M5 trail is fetched once on selection and never live-updates
  // ("SSE live-update is post-MVP", see use-trail.ts). When the dynamic
  // track-tail feature is enabled, the selected platform gets a live,
  // continuously-updating tail from :class:`TrackTailLayer` instead — so the
  // static trail stands down to avoid a stale + a live line overlapping.
  const { config: dynamicTrack } = useDynamicTrack();

  // Attach / detach the trail layer based on selection + eligibility.
  // The effect runs on every selection change AND on every trail
  // fetch-cycle transition (loading → success). On selection
  // clear, on fetch error, or on ineligibility, we detach.
  useEffect(() => {
    if (dynamicTrack.enabled) {
      // Live track-tail owns the selected platform's track; stand down.
      detachTrailLayer(map);
      return undefined;
    }
    if (selectedProducerId === null) {
      detachTrailLayer(map);
      return undefined;
    }
    const items = trailQuery.data?.items;
    if (items === undefined || items.length === 0) {
      // Fetch in flight OR returned an empty trail (a brand-new
      // Producer with no history). Detach any prior trail so the
      // surface doesn't carry stale geometry from the previous
      // selection.
      detachTrailLayer(map);
      return undefined;
    }
    // Narrow to rows with a map position for the eligibility check (TrailPoint
    // requires a position). Trail rows always carry one — the BFF trail query
    // excludes NULL-position status — so this is defensive type-safety for the
    // now-nullable StatusItem position (REQ-INGEST-044).
    const positioned = items.filter(hasMapPosition);
    if (!isTrailEligible(positioned)) {
      // Stationary Producer (sensor jitter or fixed platform). No
      // trail rendered per ADR-016 Open Item 4. The marker still
      // paints; the polyline just isn't there.
      detachTrailLayer(map);
      return undefined;
    }
    attachTrailLayer(map, items);

    // Cleanup: detach when the selection or data changes (the next
    // effect run re-attaches with fresh data). The cleanup is what
    // detaches on unmount.
    return () => {
      detachTrailLayer(map);
    };
  }, [map, selectedProducerId, trailQuery.data, dynamicTrack.enabled]);

  return null;
}
